import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import LandingPage from "./pages/LandingPage";
import HomePage from "./pages/HomePage";
import AuthPage from "./pages/AuthPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";

import AuditJournalPage from "./pages/AuditJournalPage";
import BookingConfirmationPage from "./pages/BookingConfirmationPage";
import BBQBookingPage from "./pages/BBQBookingPage";
import BBQConfirmationPage from "./pages/BBQConfirmationPage";
import ProfilePage from "./pages/ProfilePage";
import HelpPage from "./pages/HelpPage";
import KJConsolePage from "./pages/KJConsolePage";
import { Toaster } from "sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import ChatBubble from "./components/ChatBubble";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/profile`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    
    // Redirect admin users directly to admin page
    if (userData.is_admin) {
      window.location.href = '/admin';
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Auto-logout on expired token (401 response)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && localStorage.getItem('token')) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          window.location.href = '/auth';
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl font-bold">Loading...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/karaoke" element={<HomePage />} />
          <Route path="/bbq" element={<BBQBookingPage />} />
          <Route path="/bbq/confirmation/:bookingId" element={<BBQConfirmationPage />} />
          <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <AuthPage />} />
          <Route path="/password-reset" element={<PasswordResetPage />} />
          <Route path="/change-password" element={user ? <ChangePasswordPage isForced={user.is_temporary_password} /> : <Navigate to="/auth" />} />
          <Route 
            path="/dashboard" 
            element={
              user ? (
                user.is_temporary_password ? <Navigate to="/change-password" /> : <DashboardPage />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/auth" />} />
          <Route path="/admin" element={user?.is_admin ? <AdminPage /> : <Navigate to="/" />} />
          {/* Finance reports consolidated into AdminFinanceTab */}
          <Route path="/admin/audit-journal" element={user?.is_admin ? <AuditJournalPage /> : <Navigate to="/" />} />
          <Route path="/kj-console" element={user ? <KJConsolePage /> : <Navigate to="/auth" />} />
          <Route path="/booking-confirmation/:bookingId" element={<BookingConfirmationPage />} />
          <Route path="/help" element={<HelpPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <ChatBubble />
    </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
